import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowBackUp, IconCircleCheck, IconGauge } from '@tabler/icons-react';
import type { SiloReadingResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { parseNum } from '../../../lib/parseNum';
import {
  useActiveProducts,
  useCreateSiloReading,
  usePostSiloReadingReconciliation,
  useReverseSiloReading,
  useSiloLocations,
  useSiloReading,
} from '../api';
import { formatSignedKg, formatTonnes } from '../utils';
import { VarianceBadge } from './VarianceBadge';

/** Guided "silo reading" flow (BUILD_SPEC.md §8.2): pick silo → enter level →
 * system computes est. tonnes (calibration + density) vs the ledger, variance
 * highlighted → optionally post a reconciliation adjustment with a
 * plain-language preview. Step 1 (the form) IS the calculation — `POST
 * /silo/readings` snapshots the ledger qty and computes the estimate + variance
 * in one shot (services/api/src/modules/silo/readings.ts); step 2 just reviews
 * that already-computed DRAFT reading and, optionally, posts it.
 *
 * `initialLocationId`/`initialReadingId` let the silo-status overview and the
 * readings register deep-link in (pre-picked silo, or resume an existing DRAFT
 * reading that was created but not yet posted). */
export function NewReadingFlow({
  initialLocationId,
  initialReadingId,
}: {
  initialLocationId?: string;
  initialReadingId?: string;
}) {
  const canOperate = useCanOperate();
  const locationsQuery = useSiloLocations();
  const productsQuery = useActiveProducts();
  const existingReadingQuery = useSiloReading(initialReadingId);

  const [reading, setReading] = useState<SiloReadingResponse | null>(null);
  const [locationId, setLocationId] = useState<string | null>(initialLocationId ?? null);
  const [productId, setProductId] = useState<string | null>(null);
  const [levelUnit, setLevelUnit] = useState<'PCT' | 'M'>('PCT');
  const [levelValue, setLevelValue] = useState<number | string>('');
  const parsedLevelValue = parseNum(levelValue);

  const createReading = useCreateSiloReading();
  const postReconciliation = usePostSiloReadingReconciliation(reading?.id ?? '');
  const reverseReading = useReverseSiloReading(reading?.id ?? '');

  // Resume an existing DRAFT (or already-POSTED) reading loaded by id — also
  // restores the silo/product selection so step 2 can show their names.
  useEffect(() => {
    if (existingReadingQuery.data) {
      setReading(existingReadingQuery.data);
      setLocationId(existingReadingQuery.data.locationId);
      setProductId(existingReadingQuery.data.productId);
    }
  }, [existingReadingQuery.data]);

  const locations = locationsQuery.data?.items ?? [];
  const products = (productsQuery.data?.items ?? []).filter((p) => p.baseUom === 'KG');
  const selectedLocation = locations.find((l) => l.id === locationId);
  const selectedProduct = products.find((p) => p.id === productId);

  function reset() {
    setReading(null);
    setLocationId(initialLocationId ?? null);
    setProductId(null);
    setLevelValue('');
    createReading.reset();
    postReconciliation.reset();
    reverseReading.reset();
  }

  function handleCalculate() {
    if (!locationId || !productId || parsedLevelValue === null) return;
    createReading.mutate(
      {
        locationId,
        productId,
        readAt: new Date(),
        levelValue: parsedLevelValue.toFixed(2),
        levelUnit,
        source: 'MEASURED',
      },
      { onSuccess: (data) => setReading(data) },
    );
  }

  if (initialReadingId && existingReadingQuery.isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading reading…</Text>
      </Group>
    );
  }

  // --------------------------------------------------------------------
  // Step 2: review the computed estimate vs ledger + optionally post.
  // --------------------------------------------------------------------
  if (reading) {
    const varianceIsZero = Number(reading.varianceKg) === 0;
    const alreadyPosted = reading.status === 'POSTED';
    const isReversed = reading.status === 'REVERSED';
    const postResult = postReconciliation.data;

    return (
      <Stack gap="lg" maw={640}>
        <Title order={3}>2. Review &amp; post</Title>
        <Paper withBorder radius="md" p="lg">
          <Stack gap="sm">
            <Group justify="space-between">
              <div>
                <Text fw={600} size="lg">
                  {selectedLocation?.name ?? 'Silo'}
                </Text>
                <Text size="sm" c="dimmed">
                  {selectedProduct?.name ?? 'Product'} · level {reading.levelValue}
                  {reading.levelUnit === 'PCT' ? '%' : 'm'}
                </Text>
              </div>
              <VarianceBadge varianceKg={reading.varianceKg} variancePct={reading.variancePct} />
            </Group>

            <Group grow>
              <Stat label="Estimated (calibration + density)" value={formatTonnes(reading.estMassKg)} />
              <Stat label="Ledger (at read time)" value={formatTonnes(reading.ledgerQtyKg)} />
              <Stat label="Variance" value={formatSignedKg(reading.varianceKg)} />
            </Group>
            <Text size="xs" c="dimmed">
              Volume {reading.estVolumeM3} m³ at {reading.densityUsedKgM3} kg/m³
            </Text>
          </Stack>
        </Paper>

        {postResult ? (
          <Alert color="green" icon={<IconCircleCheck size={18} />} title="Posted">
            {postResult.adjustmentMovementId
              ? `Adjustment movement ${postResult.adjustmentMovementId} posted (reason: RECONCILIATION).`
              : 'No variance — the reading was marked reviewed with no ledger movement.'}
          </Alert>
        ) : isReversed ? (
          <Alert color="red" variant="light" icon={<IconArrowBackUp size={18} />} title="Reversed">
            {reading.adjustmentMovementId
              ? 'This reading has been reversed — its reconciliation adjustment was backed out by a reversing ledger entry.'
              : 'This reading has been reversed. It posted no ledger movement (zero variance), so only its status changed.'}
          </Alert>
        ) : alreadyPosted ? (
          <Alert color="green" icon={<IconCircleCheck size={18} />} title="Already posted">
            {reading.adjustmentMovementId
              ? `Adjustment movement ${reading.adjustmentMovementId} was posted for this reading.`
              : 'This reading had no variance — no ledger movement was needed.'}
          </Alert>
        ) : (
          <Paper withBorder radius="md" p="md" bg="amber.0">
            <Text size="sm">
              {varianceIsZero
                ? 'The measured level matches the ledger exactly — posting will mark this reading reviewed with no ADJUSTMENT movement.'
                : `Posting will book an ADJUSTMENT of ${formatSignedKg(reading.varianceKg)} (reason: RECONCILIATION), bringing the ledger in line with the measured level.`}
            </Text>
          </Paper>
        )}

        {postReconciliation.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not post">
            {postReconciliation.error instanceof ApiRequestError
              ? postReconciliation.error.message
              : postReconciliation.error?.message}
          </Alert>
        )}
        {reverseReading.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not reverse">
            {reverseReading.error instanceof ApiRequestError ? reverseReading.error.message : reverseReading.error?.message}
          </Alert>
        )}

        <Group>
          {!postResult && !alreadyPosted && !isReversed && (
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button disabled={!canOperate} loading={postReconciliation.isPending} onClick={() => postReconciliation.mutate()}>
                  {varianceIsZero ? 'Mark reviewed' : 'Post reconciliation adjustment'}
                </Button>
              </span>
            </Tooltip>
          )}
          {alreadyPosted && !postResult && (
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <ConfirmButton
                  color="red"
                  variant="light"
                  leftSection={<IconArrowBackUp size={16} />}
                  disabled={!canOperate}
                  loading={reverseReading.isPending}
                  label="Reverse"
                  message={
                    reading.adjustmentMovementId
                      ? `Reverse this ${selectedLocation?.name ?? 'silo'} reading? Its reconciliation adjustment will be backed out by a reversing ledger entry, restoring the ledger quantity it overwrote.`
                      : `Reverse this ${selectedLocation?.name ?? 'silo'} reading? It posted no ledger movement (zero variance), so it will just be marked REVERSED.`
                  }
                  onConfirm={() =>
                    reverseReading.mutate(undefined, { onSuccess: (result) => setReading(result.reading) })
                  }
                />
              </span>
            </Tooltip>
          )}
          <Button variant="default" onClick={reset}>
            Take another reading
          </Button>
        </Group>
      </Stack>
    );
  }

  // --------------------------------------------------------------------
  // Step 1: pick silo + product + level.
  // --------------------------------------------------------------------
  const noLocations = !locationsQuery.isLoading && locations.length === 0;

  return (
    <Stack gap="lg" maw={480}>
      <Title order={3}>1. Pick silo &amp; enter level</Title>

      {noLocations && (
        <Alert color="amber" icon={<IconAlertTriangle size={18} />}>
          No SILO locations are configured yet — ask an admin to create one before taking a reading.
        </Alert>
      )}

      <Select
        label="Silo"
        placeholder="Choose a silo"
        leftSection={<IconGauge size={16} />}
        data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
        value={locationId}
        onChange={setLocationId}
        searchable
        disabled={locationsQuery.isLoading}
      />
      <Select
        label="Product"
        placeholder="Choose the product stored in this silo"
        data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
        value={productId}
        onChange={setProductId}
        searchable
        disabled={productsQuery.isLoading}
      />
      <div>
        <Text size="sm" fw={500} mb={4}>
          Level unit
        </Text>
        <SegmentedControl
          fullWidth
          value={levelUnit}
          onChange={(v) => setLevelUnit(v as 'PCT' | 'M')}
          data={[
            { value: 'PCT', label: '% full' },
            { value: 'M', label: 'Metres' },
          ]}
        />
      </div>
      <NumberInput
        label={levelUnit === 'PCT' ? 'Level (% full)' : 'Level (metres)'}
        placeholder={levelUnit === 'PCT' ? 'e.g. 62.5' : 'e.g. 3.20'}
        min={0}
        max={levelUnit === 'PCT' ? 100 : undefined}
        decimalScale={2}
        value={levelValue}
        onChange={setLevelValue}
      />

      {createReading.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not calculate">
          {createReading.error instanceof ApiRequestError ? createReading.error.message : createReading.error?.message}
        </Alert>
      )}

      <Group>
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button
              loading={createReading.isPending}
              disabled={!canOperate || !locationId || !productId || parsedLevelValue === null}
              onClick={handleCalculate}
            >
              Calculate
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700}>{value}</Text>
    </div>
  );
}
